import React, { useState, useEffect, useCallback } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import AdminModal from '../../components/admin/AdminModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import FormField from '../../components/admin/FormField';
import Badge from '../../components/admin/Badge';
import EmptyState from '../../components/admin/EmptyState';

function AdminServers({ apiFetch, toast }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadServers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/servers');
      const data = await res.json();
      setServers(data.servers || []);
    } catch { toast.error('Error', 'Failed to load servers'); }
    finally { setLoading(false); }
  }, [apiFetch, toast]);

  useEffect(() => { loadServers(); }, [loadServers]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const isNew = !editing.id;
      const url = isNew ? '/api/admin/servers' : `/api/admin/servers/${editing.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const body = { ...editing };
      delete body.id; delete body.has_ssh_key; delete body.created_at; delete body.updated_at; delete body.current_instances;
      if (!showKey && !isNew) delete body.ssh_private_key;
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Saved', `Server ${isNew ? 'created' : 'updated'} successfully`);
      setEditing(null); setShowKey(false);
      await loadServers();
    } catch (err) { toast.error('Error', err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await apiFetch(`/api/admin/servers/${deleteConfirm}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Deleted', 'Server removed successfully');
      await loadServers();
    } catch (err) { toast.error('Error', err.message); }
    finally { setDeleteConfirm(null); }
  };

  const handleTest = async (id) => {
    setTesting(p => ({ ...p, [id]: true }));
    setTestResults(p => ({ ...p, [id]: null }));
    try {
      const res = await apiFetch(`/api/admin/servers/${id}/test-connection`, { method: 'POST' });
      const data = await res.json();
      setTestResults(p => ({ ...p, [id]: data }));
      if (data.success) toast.success('Connected', data.message);
      else toast.error('Failed', data.message);
    } catch { setTestResults(p => ({ ...p, [id]: { success: false, message: 'Network error' } })); toast.error('Error', 'Network error'); }
    finally { setTesting(p => ({ ...p, [id]: false })); }
  };

  const emptyServer = { name: '', hostname: '', ssh_port: 22, ssh_user: 'root', ssh_private_key: '', instances_base_path: '/home/ubuntu/instances', templates_base_path: '/home/ubuntu/system/templates', max_instances: 50, notes: '' };

  const capPercent = (s) => s.max_instances ? Math.round((s.current_instances / s.max_instances) * 100) : 0;
  const capClass = (p) => p < 60 ? 'low' : p < 85 ? 'medium' : 'high';

  if (loading) return <div className="admin-text-secondary">Loading servers...</div>;

  return (
    <div>
      <SectionHeader
        title="Servers"
        description="Manage deployment servers"
        actions={
          <button className="admin-btn admin-btn--primary" onClick={() => { setEditing(emptyServer); setShowKey(true); }}>
            <i className="fa-solid fa-plus" /> Add Server
          </button>
        }
      />

      {servers.length === 0 ? (
        <EmptyState
          icon="fa-solid fa-server"
          title="No servers configured"
          description="Add a deployment server to start managing your infrastructure."
          action={
            <button className="admin-btn admin-btn--primary" onClick={() => { setEditing(emptyServer); setShowKey(true); }}>
              <i className="fa-solid fa-plus" /> Add Server
            </button>
          }
        />
      ) : (
        <div className="admin-server-grid">
          {servers.map(s => {
            const pct = capPercent(s);
            return (
              <div key={s.id} className="admin-card">
                <div className="admin-flex-between">
                  <h3 className="admin-card-title">{s.name}</h3>
                  <Badge variant={s.status === 'active' ? 'active' : 'suspended'}>{s.status}</Badge>
                </div>
                <p className="admin-text-secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>{s.hostname}:{s.ssh_port}</p>
                <div style={{ marginTop: 12 }}>
                  <div className="admin-flex-between" style={{ fontSize: 12 }}>
                    <span className="admin-text-secondary">Capacity</span>
                    <span className="admin-text-secondary">{s.current_instances}/{s.max_instances} ({pct}%)</span>
                  </div>
                  <div className="admin-capacity-bar">
                    <div className={`admin-capacity-fill ${capClass(pct)}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                {testResults[s.id] && (
                  <div className={`admin-alert ${testResults[s.id].success ? 'admin-alert--success' : 'admin-alert--danger'}`} style={{ marginTop: 12 }}>
                    {testResults[s.id].message}
                    {testResults[s.id].serverInfo && (
                      <div className="admin-mono" style={{ marginTop: 4 }}>
                        Containers: {testResults[s.id].serverInfo.containerCount}
                      </div>
                    )}
                  </div>
                )}
                <div className="admin-btn-group" style={{ marginTop: 12 }}>
                  <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => handleTest(s.id)} disabled={testing[s.id]}>
                    {testing[s.id] ? 'Testing...' : 'Test'}
                  </button>
                  <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => { setEditing({ ...s }); setShowKey(false); }}>Edit</button>
                  <button className="admin-btn admin-btn--danger admin-btn--sm" onClick={() => setDeleteConfirm(s.id)}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit/Add Modal */}
      <AdminModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit Server' : 'Add Server'}
        footer={
          <>
            <button className="admin-btn admin-btn--secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="admin-btn admin-btn--primary" onClick={handleSave} disabled={saving || !editing?.name || !editing?.hostname}>{saving ? 'Saving...' : 'Save'}</button>
          </>
        }
      >
        {editing && (
          <>
            <div className="admin-grid-2">
              <FormField label="Name">
                <input className="admin-input" value={editing.name} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} placeholder="e.g. srv1" />
              </FormField>
              <FormField label="Hostname">
                <input className="admin-input" value={editing.hostname} onChange={e => setEditing(p => ({ ...p, hostname: e.target.value }))} placeholder="e.g. 10.0.0.1" />
              </FormField>
              <FormField label="SSH Port">
                <input className="admin-input" type="number" value={editing.ssh_port} onChange={e => setEditing(p => ({ ...p, ssh_port: parseInt(e.target.value) || 22 }))} />
              </FormField>
              <FormField label="SSH User">
                <input className="admin-input" value={editing.ssh_user} onChange={e => setEditing(p => ({ ...p, ssh_user: e.target.value }))} />
              </FormField>
            </div>
            {(showKey || !editing.id) ? (
              <FormField label="SSH Private Key">
                <textarea className="admin-textarea" rows={6} value={editing.ssh_private_key || ''} onChange={e => setEditing(p => ({ ...p, ssh_private_key: e.target.value }))} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" style={{ fontFamily: 'monospace', fontSize: 11 }} />
              </FormField>
            ) : (
              <FormField label="SSH Key">
                <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => setShowKey(true)}>Change SSH Key</button>
              </FormField>
            )}
            <div className="admin-grid-2">
              <FormField label="Instances Base Path">
                <input className="admin-input admin-mono" value={editing.instances_base_path || ''} onChange={e => setEditing(p => ({ ...p, instances_base_path: e.target.value }))} />
              </FormField>
              <FormField label="Templates Base Path">
                <input className="admin-input admin-mono" value={editing.templates_base_path || ''} onChange={e => setEditing(p => ({ ...p, templates_base_path: e.target.value }))} />
              </FormField>
              <FormField label="Max Instances">
                <input className="admin-input" type="number" value={editing.max_instances} onChange={e => setEditing(p => ({ ...p, max_instances: parseInt(e.target.value) || 50 }))} />
              </FormField>
            </div>
            <FormField label="Notes">
              <textarea className="admin-textarea" rows={2} value={editing.notes || ''} onChange={e => setEditing(p => ({ ...p, notes: e.target.value }))} />
            </FormField>
          </>
        )}
      </AdminModal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Server"
        message="Are you sure you want to delete this server? This action cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}

export default AdminServers;
